import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Input, Row, Select, Space, Typography } from "antd";
import { CloudDownloadOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import usePaymentCoupons from "hooks/finance/usePaymentCoupons";
import CouponsTable from "./CouponsTable";
import CouponDrawer from "./CouponDrawer";
import CouponDetailsDrawer from "./CouponDetailsDrawer";

export default function CouponsPage() {
  const navigate = useNavigate();
  const {
    coupons,
    meta,
    loading,
    fetchCoupons,
    checkCouponCode,
    createCoupon,
    updateCoupon,
    toggleCouponStatus,
  } = usePaymentCoupons();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, size: 20 });
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ search: "", status: undefined });

  useEffect(() => {
    fetchCoupons({
      page: pagination.page,
      size: pagination.size,
      search: filters.search || undefined,
      status: filters.status,
    });
  }, [fetchCoupons, filters.search, filters.status, pagination.page, pagination.size]);

  const totals = useMemo(() => {
    return coupons.reduce(
      (acc, coupon) => {
        const status = String(coupon.status || "").toUpperCase();
        if (status === "ACTIVE") acc.active += 1;
        if (status === "INACTIVE") acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 },
    );
  }, [coupons]);

  const handleCreate = () => {
    setEditingCoupon(null);
    setDrawerOpen(true);
  };

  const handleView = (coupon) => {
    setSelectedCoupon(coupon);
    setDetailsOpen(true);
  };

  const handleEdit = (coupon) => {
    setDetailsOpen(false);
    setEditingCoupon(coupon);
    setDrawerOpen(true);
  };

  const handleSubmit = async (couponId, payload) => {
    if (couponId) {
      await updateCoupon(couponId, payload);
    } else {
      await createCoupon(payload);
    }

    await fetchCoupons({
      page: pagination.page,
      size: pagination.size,
      search: filters.search || undefined,
      status: filters.status,
    });
  };

  const handleToggleStatus = async (coupon) => {
    const couponId = coupon.id || coupon.couponId || coupon.code;
    if (!couponId) return;

    const nextStatus =
      String(coupon.status || "").toUpperCase() === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    await toggleCouponStatus(couponId, nextStatus);
    await fetchCoupons({
      page: pagination.page,
      size: pagination.size,
      search: filters.search || undefined,
      status: filters.status,
    });
  };

  const handleFetchCoupons = ({ page, size }) => {
    setPagination({ page, size });
  };

  const refreshCoupons = () =>
    fetchCoupons({
      page: pagination.page,
      size: pagination.size,
      search: filters.search || undefined,
      status: filters.status,
    });

  const clearFilters = () => {
    setSearchInput("");
    setFilters({ search: "", status: undefined });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="tags"
          title="Coupons"
          subtitle="Create and manage payment coupons"
          className="mb-2 mt-2 flex-1"
        />

        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={refreshCoupons}
            style={{ borderColor: "#15803d", color: "#15803d" }}
          >
            Refresh
          </Button>
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={() => navigate(paths.paymentCouponUsages)}
            style={{ borderColor: "#dc2626", color: "#dc2626" }}
          >
            Usages
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Coupon
          </Button>
        </Space>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "10px 12px",
          marginBottom: 10,
          background: "#fff",
        }}
      >
        <Row gutter={[10, 10]} style={{ alignItems: "end" }}>
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
              Search
            </Typography.Text>
            <Input.Search
              allowClear
              placeholder="Search by code or name"
              value={searchInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSearchInput(nextValue);
                if (!nextValue) {
                  setFilters((prev) => ({ ...prev, search: "" }));
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }
              }}
              onSearch={(value) => {
                setFilters((prev) => ({ ...prev, search: value.trim() }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
              Status
            </Typography.Text>
            <Select
              allowClear
              placeholder="All statuses"
              value={filters.status}
              style={{ width: "100%" }}
              options={[
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
                { value: "EXPIRED", label: "Expired" },
              ]}
              onChange={(status) => {
                setFilters((prev) => ({ ...prev, status }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Space wrap>
              <Button onClick={clearFilters}>Reset</Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {totals.active} active, {totals.inactive} inactive on this page
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      <CouponsTable
        coupons={coupons}
        loading={loading}
        meta={meta}
        onFetchCoupons={handleFetchCoupons}
        onView={handleView}
        onEdit={handleEdit}
        onToggleStatus={handleToggleStatus}
      />

      <CouponDetailsDrawer
        open={detailsOpen}
        coupon={selectedCoupon}
        onClose={() => setDetailsOpen(false)}
        onEdit={handleEdit}
      />

      <CouponDrawer
        open={drawerOpen}
        coupon={editingCoupon}
        onClose={() => setDrawerOpen(false)}
        onCheckCode={checkCouponCode}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
